import React from 'react';
import { Box, Button, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import CheckIcon from '@mui/icons-material/Check';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import { WordStatusServer } from 'app/types';
import { STATUS_META } from 'components/dictionaryView/statusMeta';
import { DESKTOP_LAYOUT_BREAKPOINT } from 'pages/VocabularyTest/utils';

interface IStatusActionsBarProps {
  hideLabel: boolean;
  selectedCount: number;
  onApplyStatus: (status: WordStatusServer) => void;
  onClearSelection: () => void;
}

/**
 * "Не вивчено" (NEW) has no entry in STATUS_META — that palette models the
 * dictionary's "not in vocabulary at all" purple, a different concept from
 * the test's neutral "not yet marked" state. Kept local to this bar.
 */
const NOT_LEARNED_HUE = '#8A8F99';

const STATUS_BUTTONS: {
  key: WordStatusServer;
  label: string;
  hue: string;
  Icon: typeof RecordVoiceOverIcon;
}[] = [
  { key: 'ACTIVE', label: 'Активне', hue: STATUS_META.active.color, Icon: RecordVoiceOverIcon },
  { key: 'LEARNED', label: 'Вивчено', hue: STATUS_META.learned.color, Icon: CheckIcon },
  { key: 'REPEAT', label: 'Повторити', hue: STATUS_META.repeat.color, Icon: RefreshIcon },
  { key: 'NEW', label: 'Не вивчено', hue: NOT_LEARNED_HUE, Icon: CloseIcon },
];

const StatusActionsBar: React.FC<IStatusActionsBarProps> = ({
  hideLabel,
  selectedCount,
  onApplyStatus,
  onClearSelection,
}) => {
  const armed = selectedCount > 0;
  const theme = useTheme();
  // Stack's `direction`/`spacing` props only resolve responsive objects
  // against the theme's named breakpoints (xs/sm/md/lg/xl) — DESKTOP_LAYOUT_
  // BREAKPOINT is a deliberately custom value between md and lg, so it has
  // to be read as a real media query instead.
  const isDesktop = useMediaQuery(theme.breakpoints.up(DESKTOP_LAYOUT_BREAKPOINT));

  return (
    <Stack
      alignItems={'center'}
      direction={isDesktop ? 'row' : 'column'}
      spacing={isDesktop ? 1.5 : 1}
      sx={{ flexWrap: 'wrap', justifyContent: 'center', rowGap: 1, width: isDesktop ? 'auto' : '100%' }}
    >
      <Typography
        sx={(theme) => ({
          alignItems: 'center',
          color: armed ? 'primary.main' : 'text.secondary',
          // On mobile/tablet the label stacks in its own row (direction:
          // column above), so opacity:0 alone still reserves a full row of
          // height — display:none collapses it away entirely once hidden.
          // Desktop keeps the opacity-only fade: it sits inline in a row
          // there, so the reserved width is what keeps the button group
          // centered and hiding it doesn't add any extra height to begin
          // with.
          display: hideLabel ? 'none' : 'inline-flex',
          fontWeight: armed ? 700 : 400,
          gap: 0.75,
          justifyContent: 'center',
          opacity: 1,
          textAlign: 'center',
          transition: theme.transitions.create('opacity', {
            duration: theme.transitions.duration.shorter,
          }),
          whiteSpace: 'nowrap',
          width: '100%',
          [theme.breakpoints.up(DESKTOP_LAYOUT_BREAKPOINT)]: {
            display: 'inline-flex',
            justifyContent: 'flex-end',
            opacity: hideLabel ? 0 : 1,
            textAlign: 'right',
            width: 258,
          },
        })}
      >
        {armed && <CheckBoxIcon fontSize={'small'} />}
        {armed
          ? `Обрано ${selectedCount} — присвоїти:`
          : 'Познач слова, потім обери статус:'}
      </Typography>

      {/* Two-column CSS grid below the desktop cutoff — flex-wrap with
          percentage bases didn't keep columns pixel-aligned across rows
          (button widths vary with their label length: "Активне" vs
          "Не вивчено"), so the grid rows visibly staggered. Grid tracks are
          exact, so both columns always line up. Desktop keeps the natural
          flex row (4 fixed-size buttons in one line, no alignment concern). */}
      <Box
        sx={(theme) => ({
          display: 'grid',
          gap: 1,
          gridTemplateColumns: '1fr 1fr',
          justifyContent: 'center',
          width: '100%',
          [theme.breakpoints.up(DESKTOP_LAYOUT_BREAKPOINT)]: {
            display: 'flex',
            gridTemplateColumns: 'none',
            width: 'auto',
          },
        })}
      >
        {STATUS_BUTTONS.map(({ Icon, ...status }) => (
          <Button
            disabled={!armed}
            key={status.key}
            onClick={() => onApplyStatus(status.key)}
            startIcon={<Icon sx={{ color: armed ? 'common.white' : status.hue }} />}
            sx={{
              backgroundColor: armed ? status.hue : alpha(status.hue, 0.12),
              color: armed ? 'common.white' : 'text.primary',
              fontWeight: 700,
              '&:hover': { backgroundColor: status.hue, opacity: 0.92 },
              '&.Mui-disabled': {
                backgroundColor: alpha(status.hue, 0.12),
                color: 'text.primary',
              },
            }}
          >
            {status.label}
          </Button>
        ))}
      </Box>

      <Box
        sx={(theme) => ({
          display: 'none',
          justifyContent: 'flex-start',
          width: 130,
          [theme.breakpoints.up(DESKTOP_LAYOUT_BREAKPOINT)]: {
            display: 'flex',
          },
        })}
      >
        {armed && (
          <Button onClick={onClearSelection} sx={{ color: 'text.secondary' }}>
            {'Зняти вибір'}
          </Button>
        )}
      </Box>
    </Stack>
  );
};

export default StatusActionsBar;
